import {
  INACTIVITY_WARN_DAYS, PLAN_PRICE_CENTS, PLAN_USER_LIMIT, formatEur, parseDbTimestamp, planPriceCents,
  type WorkshopStatus,
} from '@wf/shared'
import type { TipLine } from '../ui/InfoTip'

// Anzeige-Logik des Betreiber-Dashboards (rein, getestet): Status-/Tarif-Beschriftung + Zeit-Labels.
// Die WERTE (Preise, Seats, Warn-Schwelle) kommen aus @wf/shared — hier steht nur Text, nie eine zweite Wahrheit.

export const STATUS_LABELS: Record<WorkshopStatus, string> = {
  active: 'Aktiv',
  suspended: 'Gesperrt',
  canceled: 'Gekündigt',
}

/** Chip-Modifier je Status (Styles: .wf-chip--st-* in styles.css). */
export const STATUS_CHIP: Record<WorkshopStatus, string> = {
  active: 'wf-chip--st-active',
  suspended: 'wf-chip--st-suspended',
  canceled: 'wf-chip--st-canceled',
}

/** Tooltip-Zeilen „Lizenz-Status" — erklärt dem Betreiber, was jeder Zustand konkret bewirkt. */
export const STATUS_TIP_LINES: TipLine[] = [
  { label: 'Aktiv', text: 'Normalbetrieb — die Werkstatt kann sich anmelden und arbeiten; zählt in MRR/Lizenzen.' },
  { label: 'Gesperrt', text: 'Vorübergehend gesperrt (z. B. Zahlungsverzug): Login UND laufende Sessions sind sofort zu, alle Daten bleiben. Jederzeit wieder freigebbar.', warn: true },
  { label: 'Gekündigt', text: 'Vertrag beendet: Zugang dauerhaft zu, Daten bleiben für gesetzliche Fristen (GoBD/DSGVO) erhalten, bis sie gelöscht werden.', warn: true },
]

/** Tooltip-Zeilen „Tarif" — Preis/Monat + Nutzer-Limit je Tarif, direkt aus der shared-Wahrheit generiert. */
export function planTipLines(): TipLine[] {
  return Object.keys(PLAN_PRICE_CENTS).map((plan) => {
    const seats = PLAN_USER_LIMIT[plan]
    const seatText = seats != null && seats < 9999 ? `bis ${seats} Nutzer` : 'unbegrenzte Nutzer'
    const price = planPriceCents(plan)
    return { label: plan, text: `${price === 0 ? 'kostenlos' : `${formatEur(price)}/Monat`}, ${seatText}.` }
  })
}

const DAY_MS = 24 * 3600 * 1000

/** Laufzeit seit Anlage: „seit heute" / „seit 12 Tagen" / „seit 3 Mon." / „seit 1 J. 2 Mon." */
export function laufzeitLabel(createdAt: string, nowMs = Date.now()): string {
  const days = Math.floor((nowMs - parseDbTimestamp(createdAt)) / DAY_MS)
  if (days <= 0) return 'seit heute'
  if (days < 60) return `seit ${days} ${days === 1 ? 'Tag' : 'Tagen'}`
  const months = Math.floor(days / 30)
  if (months < 12) return `seit ${months} Mon.`
  const years = Math.floor(months / 12)
  const rest = months % 12
  return rest === 0 ? `seit ${years} J.` : `seit ${years} J. ${rest} Mon.`
}

/** Letzter Login: „heute" / „gestern" / „vor 5 T" / „—" (nie eingeloggt). */
export function lastSeenLabel(lastSeenAt: string | null, nowMs = Date.now()): string {
  if (!lastSeenAt) return '—'
  const days = Math.floor((nowMs - parseDbTimestamp(lastSeenAt)) / DAY_MS)
  if (days <= 0) return 'heute'
  if (days === 1) return 'gestern'
  return `vor ${days} T`
}

/** Churn-Risiko-Warnung: AKTIVE Werkstatt ohne Login seit ≥ INACTIVITY_WARN_DAYS (oder noch nie). */
export function inactivityWarning(status: WorkshopStatus, lastSeenAt: string | null, nowMs = Date.now()): string | null {
  if (status !== 'active') return null
  if (!lastSeenAt) return 'noch nie eingeloggt'
  const days = Math.floor((nowMs - parseDbTimestamp(lastSeenAt)) / DAY_MS)
  return days >= INACTIVITY_WARN_DAYS ? `seit ${days} Tagen inaktiv` : null
}
